// A term line's five clauses, as one sentence a person can check.
//
// "counts minutes from transcripts where escalation_kind is not instant, at
// USD 0.80 each, 100% ours, quarterly in arrears."
//
// The alternative is what the screen used to show: a JSON dump of the selector
// beside four enum values. A rule that bills money has to be READABLE by the
// person who agreed to it, and reading `{"kind":"ne","left":{"kind":"field",
// "name":"escalation_kind"},"right":{"kind":"str","value":"instant"}}` is not
// checking a contract, it is decoding one.
//
// A MODULE rather than a component, for the reason `calendarEvents.ts` is one:
// what a clause says is a decision about what a reader needs, and it is
// checkable as a string in a way it is not as JSX.
//
// Every switch is closed by `assertNever`, so a node kind added to the contract
// is a compile error here rather than a clause that quietly renders as nothing.

import type { Currency, MeterExpr, TermLine, TermLinePreview } from "@crm/shared";
import { formatMoney } from "../../format/money.js";
import {
  billingTimingLabel,
  cadenceLabel,
  meterSourceLabel,
  shareKindLabel,
} from "../../format/labels.js";
import { assertNever } from "../../lib/assertNever.js";

/** The clauses a sentence reads. A saved line and an unsaved draft both fit. */
export type SentenceLine = Pick<
  TermLinePreview,
  | "source"
  | "where"
  | "quantity"
  | "unit"
  | "price_source"
  | "rate_minor"
  | "amount_minor"
  | "share_kind"
  | "share_override_pct"
  | "cadence"
  | "billing_timing"
>;

/** An expression as a phrase. Parenthesised only where nesting needs it. */
export function describeExpr(expr: MeterExpr): string {
  switch (expr.kind) {
    case "num":
      return String(expr.value);
    case "str":
      return expr.value;
    case "field":
      return expr.name;
    case "mul":
      return `${describeExpr(expr.left)} times ${describeExpr(expr.right)}`;
    case "div":
      return `${describeExpr(expr.left)} divided by ${describeExpr(expr.right)}`;
    case "eq":
      return `${describeExpr(expr.left)} is ${describeExpr(expr.right)}`;
    case "ne":
      return `${describeExpr(expr.left)} is not ${describeExpr(expr.right)}`;
    case "lt":
      return `${describeExpr(expr.left)} is below ${describeExpr(expr.right)}`;
    case "lte":
      return `${describeExpr(expr.left)} is at most ${describeExpr(expr.right)}`;
    case "gt":
      return `${describeExpr(expr.left)} is above ${describeExpr(expr.right)}`;
    case "gte":
      return `${describeExpr(expr.left)} is at least ${describeExpr(expr.right)}`;
    case "and":
      return expr.operands.map(nested).join(" and ");
    case "or":
      return expr.operands.map(nested).join(" or ");
    case "not":
      return `not ${nested(expr.operand)}`;
    case "in":
      return `${describeExpr(expr.value)} is one of ${expr.options.join(", ")}`;
    case "has_function":
      return `the call runs ${expr.names.join(" or ")}`;
    default:
      return assertNever(expr, "term line expression kind");
  }
}

/**
 * A branch inside a branch, bracketed.
 *
 * A leaf never needs brackets and every operator does: "a and b or c" reads two
 * ways, and a rule that bills money may not be ambiguous on the page.
 */
function nested(expr: MeterExpr): string {
  switch (expr.kind) {
    case "num":
    case "str":
    case "field":
      return describeExpr(expr);
    default:
      return `(${describeExpr(expr)})`;
  }
}

/**
 * Whether this clause has to COUNT before anything can price it.
 *
 * The same question the interpretation asks (`crmNeedsQuantity`): a rate is
 * per unit, and a measured fact that states money still states it per unit. A
 * flat clause counts the period, and a provider's own fact is already money.
 */
function mustCount(line: SentenceLine): boolean {
  switch (line.price_source) {
    case "contract_rate":
      return true;
    case "flat_per_period":
      return false;
    case "fact_amount":
      return line.source === "usage" || line.source === "transcripts";
    default:
      return assertNever(line.price_source, "price source");
  }
}

/**
 * What the clause counts.
 *
 * A clause that must count and has NO rule counts nothing: the server reports
 * `rule_missing` over every fact it matched and bills none of them. So the
 * sentence says the rule is missing. "One unit per period" would be a figure
 * the invoice is never going to carry.
 *
 * A deal-sourced clause with no rule never reaches here: `statedFactClause`
 * answers for it, because the deal fact that names the clause carries the
 * number and there is nothing to count.
 */
function countedClause(line: SentenceLine): string {
  if (line.price_source === "flat_per_period") return "counts the period itself";
  if (line.quantity !== null && line.quantity !== undefined) {
    return `counts ${describeExpr(line.quantity)} as ${line.unit}`;
  }
  return mustCount(line) ? "has no quantity rule, so it counts nothing" : "counts no units at all";
}

/** "counts minutes from transcripts where escalation_kind is not instant". */
function selectorClause(line: SentenceLine): string {
  const counted = countedClause(line);
  if (line.source === "deal") {
    return `${counted} from the facts recorded against this deal`;
  }
  const where =
    line.where === null || line.where === undefined ? "" : ` where ${describeExpr(line.where)}`;
  return `${counted} from ${meterSourceLabel(line.source)}${where}`;
}

/** "at USD 0.80 each" / "USD 1,250.00 per period" / "at whatever the facts say". */
function priceClause(line: SentenceLine, currency: Currency): string {
  switch (line.price_source) {
    case "contract_rate":
      return line.rate_minor === null || line.rate_minor === undefined
        ? "at a rate nobody has set"
        : `at ${formatMoney(line.rate_minor, currency)} each`;
    case "flat_per_period":
      return line.amount_minor === null || line.amount_minor === undefined
        ? "for an amount nobody has set"
        : `${formatMoney(line.amount_minor, currency)} per period`;
    case "fact_amount":
      return factPriceClause(line.source);
    default:
      return assertNever(line.price_source, "price source");
  }
}

/**
 * WHICH facts state the money, because the four sources do not state it alike.
 *
 * One sentence for all four said something untrue about three of them. A usage
 * fact carries units, so a price turns them into money. A provider cost fact
 * carries the amount already. A deal fact carries the amount somebody stated
 * for the month.
 *
 * A transcript carries neither, and the SERVER is the authority on that: it
 * tallies transcripts with no price table at all (`crmTallyLine` passes
 * `prices = null`), so such a clause prices nothing and counts every fact it
 * matched as unpriced. The sentence says so rather than promise a figure.
 */
function factPriceClause(source: SentenceLine["source"]): string {
  switch (source) {
    // The rate card prices the units. Where a provider cost fact covers the
    // fact's own day that arm answers first; the statement line names the arm
    // that priced each fact.
    case "usage":
      return "priced per unit from the rate card";
    case "transcripts":
      return "at a figure transcripts cannot state";
    case "provider_cost":
      return "at the amount the provider's cost feed states";
    case "deal":
      return "at the amount a person states each period";
    default:
      return assertNever(source, "term line source");
  }
}

/** "100% ours" / "our recurring split". */
function shareClause(line: SentenceLine): string {
  const override = line.share_override_pct;
  return override === null || override === undefined
    ? `our ${shareKindLabel(line.share_kind).toLowerCase()}`
    : `${override}% ours`;
}

/** "quarterly in arrears". */
function periodClause(line: SentenceLine): string {
  const cadence = cadenceLabel(line.cadence).toLowerCase();
  const timing = line.billing_timing ?? "arrears";
  return line.cadence === "milestone"
    ? "billed as its milestones land"
    : `${cadence} ${billingTimingLabel(timing)}`;
}

/**
 * What a deal-sourced clause reads, or null when the clause is not one.
 *
 * A DEAL FACT IS THE FIGURE. Nobody selects it and nothing counts it: a person
 * states the amount for the month, or states the quantity the paper's rate
 * prices. So the sentence leads with that fact and drops the selector. The two
 * clauses together read as "counts the currency_minor count a fact states from
 * the facts recorded against this deal, at the amount a person states each
 * period" — machinery this clause does not have, and a unit that is a storage
 * detail.
 *
 * A clause that carries a quantity RULE is not this case, however it is
 * sourced: the rule reads the facts, so the ordinary selector states it.
 */
function statedFactClause(line: SentenceLine, currency: Currency): string | null {
  if (line.source !== "deal") return null;
  if (line.quantity !== null && line.quantity !== undefined) return null;
  switch (line.price_source) {
    case "fact_amount":
      return "the amount a person states each period";
    case "contract_rate":
      return `the quantity a person states each period × ${rateWords(line, currency)}`;
    // A flat clause takes its amount from the paper and reads no fact at all.
    // The ordinary sentence already says that in the right words.
    case "flat_per_period":
      return null;
    default:
      return assertNever(line.price_source, "price source");
  }
}

/** "$0.80 USD", or the words for a clause whose rate nobody has set. */
function rateWords(line: SentenceLine, currency: Currency): string {
  return line.rate_minor === null || line.rate_minor === undefined
    ? "a rate nobody has set"
    : formatMoney(line.rate_minor, currency);
}

/** The whole line, as one sentence. */
export function termLineSentence(line: SentenceLine, currency: Currency): string {
  const stated = statedFactClause(line, currency);
  const head = stated === null ? [selectorClause(line), priceClause(line, currency)] : [stated];
  return [...head, shareClause(line), periodClause(line)].join(", ");
}

/** The saved row's own sentence, in the deal's currency. */
export function savedTermLineSentence(line: TermLine): string {
  return termLineSentence(line, line.currency);
}
